import { COLOURS, MAX_LABEL, MAX_ROIS } from './roi';
import { captureTime } from './series';

/*
 * An experiment's ROI template (experiments.thermal_roi_template, migration
 * 0096; docs/FIELD_AI_VISION_SPEC.md section 43): the labels, colours and
 * reference every capture of the experiment is measured with, so the same
 * ROI is comparable across time.
 *
 *   templateProblem(rows)        why a template being edited cannot be saved, or null
 *   nextTemplateEntry(t, rois)   the first template label no ROI carries yet: what
 *                                the Thermal Analysis Panel names the next shape drawn
 *   seedFor(captures, id, t)     what the panel opens a capture with that has no
 *                                analysis yet: the template's ROIs placed where they
 *                                were on the newest earlier capture of the same unit,
 *                                ready to adjust -- {rois, referenceLabel} or null
 *
 * Pure: no React, no request.
 */
const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/** 'empty' | 'tooMany' | 'label' | 'duplicate' | 'colour' | 'reference' | null */
export function templateProblem(rows, referenceLabel = null) {
  if (!rows?.length) return 'empty';
  if (rows.length > MAX_ROIS) return 'tooMany';
  const seen = [];
  for (const r of rows) {
    const label = String(r.label || '').trim().replace(/\s+/g, ' ');
    if (!label || label.length > MAX_LABEL) return 'label';
    if (seen.some((s) => same(s, label))) return 'duplicate';
    seen.push(label);
    if (r.colour && !/^#[0-9A-Fa-f]{6}$/.test(r.colour)) return 'colour';
  }
  if (referenceLabel && !seen.some((s) => same(s, referenceLabel))) return 'reference';
  return null;
}

/** The template as PUT /experiments/{id}/thermal-template takes it. */
export function templateBody(rows, referenceLabel = null) {
  return {
    rois: rows.map((r, i) => ({ label: String(r.label).trim().replace(/\s+/g, ' '),
      colour: r.colour || COLOURS[i % COLOURS.length] })),
    reference_label: referenceLabel || null,
  };
}

export function nextTemplateEntry(template, rois) {
  return (template?.rois || []).find((t) => !(rois || []).some((r) => same(r.label, t.label))) || null;
}

export function seedFor(captures, captureId, template) {
  const list = captures || [];
  const target = list.find((c) => c.capture_id === captureId);
  if (!target || target.analysis) return null;
  const units = new Set(target.unit_ids || []);
  const t = captureTime(target);
  const earlier = list
    .filter((c) => c.capture_id !== captureId && c.analysis?.rois?.length
      && (c.unit_ids || []).some((u) => units.has(u))
      && (t === null || captureTime(c) === null || captureTime(c) <= t))
    .sort((a, b) => (captureTime(b) ?? 0) - (captureTime(a) ?? 0));
  const previous = earlier[0]?.analysis;
  const wanted = template?.rois || [];
  if (!previous) return null;
  const rois = previous.rois
    .filter((r) => !wanted.length || wanted.some((w) => same(w.label, r.label)))
    .map((r) => {
      const entry = wanted.find((w) => same(w.label, r.label));
      return { label: entry?.label || r.label, colour: entry?.colour || r.colour, shape: r.shape, geometry: r.geometry };
    });
  if (!rois.length) return null;
  const reference = template?.reference_label || previous.reference_label || null;
  return { rois, referenceLabel: rois.some((r) => same(r.label, reference)) ? reference : null };
}
